
import { createRouter, createWebHistory } from 'vue-router';
import psl from 'psl';

import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { useSessionsStore } from '@/stores/sessions';
import { useCoursesStore } from '@/stores/courses';

import { destroyUrlReferer, getUrlReferer, saveUrlReferer } from '@/common/referer.service';
import { saveToken } from '@/common/jwt.service';
import i18n from '@/i18n';
import { ROLE_ADMIN_PERFORM, ROLE_PLAYER } from '@/common/userRoles';
import { getOrganization, getOrganizations } from '@/common/organization.service';
import { API_URL, REDIRECT_URI } from '@/common/config';

const GuardRoute = {
  render: () => null,
};

const routes = [
  {
    path: '/my-organization',
    name: 'Homepage',
    component: () => import(/* webpackChunkName: "about" */ '../views/Homepage.vue'),
    beforeEnter: async (to, from, next) => {
      const sessionsStore = useSessionsStore();
      if (sessionsStore.sessions.length === 0) {
        await sessionsStore.fetchSessions();
      }
      next();
    },
    meta: {
      needGuard: true,
      needOrganization: true,
      title: 'Homepage',
    },
  },
  {
    path: '/my-organization/sessions/:sessionId',
    component: () => import(/* webpackChunkName: "about" */ '../views/SessionView.vue'),
    beforeEnter: async (to, from, next) => {
      const sessionsStore = useSessionsStore();
      try {
        if (sessionsStore.sessions.length === 0) {
          await sessionsStore.fetchSessions();
        }
        await sessionsStore.selectSession(to.params.sessionId);
        if (to.params.courseId === undefined || to.params.courseId === null || to.params.courseId === '') {
          next({ name: 'CourseView', params: { sessionId: to.params.sessionId, courseId: 0 } });
        } else {
          next();
        }
      } catch {
        next({ name: 'Homepage' });
      }
    },
    name: 'SessionView',
    meta: {
      needGuard: true,
      needOrganization: true,
    },
    children: [
      {
        path: ':courseId?',
        component: () => import(/* webpackChunkName: "about" */ '../views/CourseView.vue'),
        name: 'CourseView',
        beforeEnter: async (to, from, next) => {
          const coursesStore = useCoursesStore();
          try {
            const courseResponse = await coursesStore.fetchCourses();
            const course = (!to.params.courseId || to.params.courseId === 0 || to.params.courseId === '0')
              ? courseResponse.data.lastCourseId
              : to.params.courseId;
            await coursesStore.selectCourse(course);
            next();
          } catch {
            next({ name: 'Homepage' });
          }
        },
      },
    ],
  },
  {
    path: '/fromperform',
    component: GuardRoute,
    beforeEnter: async (to, from, next) => {
      const sessionsStore = useSessionsStore();
      const coursesStore = useCoursesStore();
      const organizationStore = useOrganizationStore();
      sessionsStore.setCurrentSession(null);
      coursesStore.setCurrentCourse(null);

      const orgId = to.query.id ?? to.query.orgId;
      const sessionId = to.query.session ?? to.query.sessionId;

      if (!orgId) {
        next({ name: 'Organizations' });
        return;
      }

      await organizationStore.fetchOrganizations();
      organizationStore.setOrganizationId(orgId);

      if (sessionId) {
        next({
          path: `/my-organization/sessions/${sessionId}`,
          query: to.query,
        });
      } else {
        next({ name: 'Homepage' });
      }
    },
    meta: {
      needGuard: true,
      title: 'Go to instance',
    },
  },
  {
    path: '/fromperform/:id',
    component: GuardRoute,
    beforeEnter: async (to, from, next) => {
      const sessionsStore = useSessionsStore();
      const coursesStore = useCoursesStore();
      const organizationStore = useOrganizationStore();
      sessionsStore.setCurrentSession(null);
      coursesStore.setCurrentCourse(null);
      const orgId = to.params.id;
      await organizationStore.fetchOrganizations();
      organizationStore.setOrganizationId(orgId);
      next({ name: 'Homepage' });
    },
    meta: {
      needGuard: true,
      title: 'Go to instance',
    },
  },
  {
    path: '/fromperform/:id/:session',
    component: GuardRoute,
    beforeEnter: async (to, from, next) => {
      const sessionsStore = useSessionsStore();
      const coursesStore = useCoursesStore();
      const organizationStore = useOrganizationStore();
      sessionsStore.setCurrentSession(null);
      coursesStore.setCurrentCourse(null);
      const orgId = to.params.id;
      const sessionId = to.params.session;
      await organizationStore.fetchOrganizations();
      organizationStore.setOrganizationId(orgId);
      next({
        path: `/my-organization/sessions/${sessionId}`,
        query: to.query,
      });
    },
    meta: {
      needGuard: true,
      title: 'Go to session',
    },
  },
  {
    path: '/invitetosession/:code',
    name: 'invitetosession',
    component: GuardRoute,
    beforeEnter: async (to, from, next) => {
      const sessionsStore = useSessionsStore();
      const coursesStore = useCoursesStore();
      const organizationStore = useOrganizationStore();
      try {
        sessionsStore.setCurrentSession(null);
        coursesStore.setCurrentCourse(null);
        const data = await sessionsStore.inviteToSession(to.params.code);
        if (data.status !== 'success') {
          const errorCode = data.code || 4000;
          window.sessionStorage.setItem('invite_result', JSON.stringify({ status: 'error', code: errorCode }));
          next({ name: 'Join-Session', query: { status: 'error', code: errorCode } });
          return;
        }

        const { sessionId, orgId } = data.data;
        try {
          await organizationStore.fetchOrganizations();
        } catch (error) {
          // Do not block successful invitation if organizations refresh fails.
        }
        organizationStore.setOrganizationId(orgId);
        window.sessionStorage.setItem('invite_result', JSON.stringify({ status: 'success', session: String(sessionId) }));
        next({ name: 'Join-Session', query: { status: 'success', session: sessionId } });
      } catch (error) {
        const responseCode = error?.response?.data?.code || 4000;
        window.sessionStorage.setItem('invite_result', JSON.stringify({ status: 'error', code: responseCode }));
        next({ name: 'Join-Session', query: { status: 'error', code: responseCode } });
      }
    },
    meta: {
      needGuard: true,
      title: 'Invite To Session',
    },
  },
  {
    path: '/samlredirect/:token',
    name: 'redirect',
    component: GuardRoute,
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore();
      saveToken(to.params.token);
      await authStore.checkAuth();
      const refererUrl = getUrlReferer();
      next({ path: refererUrl });
    },
    meta: {
      title: 'SAML Redirect',
    },
  },
  {
    path: '/',
    name: 'Organizations',
    component: () => import(/* webpackChunkName: "about" */ '../views/Organizations.vue'),
    meta: {
      needGuard: true,
      title: 'My organizations',
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: GuardRoute,
    meta: {
      needGuard: false,
      title: 'logout',
    },
    beforeEnter: () => {
      const authStore = useAuthStore();
      authStore.doLogout();
    },
  },
  {
    path: '/callback-login',
    name: 'Callback-login',
    component: GuardRoute,
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore();
      try {
        await authStore.callbackLogin({ code: to.query.code, state: to.query.state });
        const refererUrl = getUrlReferer();
        if (refererUrl) {
          destroyUrlReferer();
          return next(refererUrl);
        }
        return next('/');
      } catch {
        authStore.doLogout();
      }
    },
    meta: {
      needGuard: false,
      title: 'login',
    },
  },
  {
    path: '/403',
    name: 'AccessDenied',
    component: () => import('../views/AccessDenied.vue'),
    meta: {
      needGuard: false,
      title: 'AccessDenied',
    },
  },
  {
    path: '/login',
    name: 'Custom Login',
    component: () => import('../views/CustomLogin.vue'),
    beforeEnter: (to, from, next) => {
      const organizationStore = useOrganizationStore();
      const authStore = useAuthStore();
      if (!organizationStore.organization || !organizationStore.organizationId || !organizationStore.organizations) {
        next('/');
      } else {
        authStore.purgeAuth();
        const { organization } = organizationStore;
        let redirectUrl = null;
        const forcedIdp = organization.idps.find((idp) => idp.forceUse);
        if (forcedIdp) {
          redirectUrl = `${API_URL}/saml/${forcedIdp.identifier}/login`;
        }
        if (redirectUrl) {
          window.location.replace(redirectUrl);
        } else {
          next();
        }
      }
    },
    meta: {
      needGuard: false,
      needOrganization: true,
      title: 'Login',
    },
  },
  {
    path: '/JoinSession',
    name: 'Join-Session',
    component: () => import('../views/JoinSession.vue'),
    meta: {
      needGuard: true,
      title: 'Join Session',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "about" */ '../views/NotFound.vue'),
    meta: {
      needGuard: false,
      title: 'NotFound',
    },
  },
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

let domainVerified = false;
let orgVerified = false;
let customDomain = false;

function changeFavicon(fullDomain) {
  const parsed = psl.parse(fullDomain);
  const domain = parsed.domain ?? window.location.hostname;
  if (domain && domain !== 'vtsperform.com') {
    const favIcon = document.getElementById('favicon');
    const url = `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&size=16&url=https://${domain}`;
    if(favIcon) {
        favIcon.setAttribute('href', url);
    }
  }
}

router.beforeEach(async (to, from, next) => {
  const organizationStore = useOrganizationStore();
  const authStore = useAuthStore();

  if (!domainVerified) {
    const domain = window.location.hostname;
    const performURL = new URL(REDIRECT_URI);
    if (domain !== performURL.hostname) {
      changeFavicon(domain);
      try {
        await organizationStore.checkOrganizationDomain({ domain });
        domainVerified = true;
        customDomain = true;
      } catch {
        domainVerified = true;
        next('/403');
        return;
      }
    } else {
      domainVerified = true;
      customDomain = false;
    }
  }

  if (to.matched.some((m) => m.meta.needGuard)) {
    if (to.query.token) {
      saveToken(to.query.token);
    }
    if (to.query.login) {
      authStore.setLogin(to.query.login);
    }
    if (to.query.password) {
      authStore.setPassword(to.query.password);
    }

    try {
      await authStore.checkAuth();
      i18n.global.locale.value = authStore.locale;
      const { roles } = authStore.user;
      if (roles.indexOf(ROLE_ADMIN_PERFORM) === -1 && roles.indexOf(ROLE_PLAYER) === -1) {
        next('/403');
        return;
      }

      const needOrganization = to.matched.some((m) => m.meta.needOrganization);

      if (needOrganization) {
        if (!organizationStore.organization || !organizationStore.organizationId || !organizationStore.organizations) {
          const organization = getOrganization();
          const organizations = getOrganizations();
          if (organization && organizations) {
            organizationStore.setOrganization(organization);
            organizationStore.setOrganizations(organizations);
          } else {
            next({ name: 'Organizations' });
            return;
          }
        }
        if (!orgVerified) {
            try {
                await organizationStore.checkOrganizationAccess();
                orgVerified = true;
            } catch {
                orgVerified = true;
                next('/403');
                return;
            }
        }
      }

      if (!needOrganization || orgVerified) {
        if (customDomain && to.path === '/') {
          next('/my-organization');
        } else {
          next();
        }
      } else {
        next();
      }

    } catch {
      if (to.query.portal) {
        authStore.setPortal(to.query.portal);
      }
      if (customDomain) {
        authStore.setReferer(to.fullPath);
        const lang = navigator.language.split('-')[0];
        i18n.global.locale.value = lang;
        next({ path: '/login' });
      } else {
        saveUrlReferer(to.fullPath);
        authStore.doLogin();
      }
    }
  } else {
    next();
  }
});

export default router;
